import { type FC } from "react";
import { RefreshCw, Loader2, Check } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/shared/components/ui/tooltip";

import type { PlaidConnection } from "@/shared/types/estateTypes";

interface SyncStatusProps {
  connection: PlaidConnection;
  onRefresh: () => void;
  onReconnect: () => void;
  isRefreshing: boolean;
  refreshResult?: { refreshed_count: number } | null;
}

export const SyncStatus: FC<SyncStatusProps> = ({ connection, onRefresh, onReconnect, isRefreshing, refreshResult }) => {
  const isFailed = connection.status === "error";
  const isDone = !isRefreshing && !!refreshResult;
  const isSyncing = isRefreshing;

  const timeAgo = connection.last_refreshed_at
    ? formatDistanceToNow(new Date(connection.last_refreshed_at), { addSuffix: true })
    : "Not synced yet";

  if (isSyncing) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-secondary" />
        <span className="text-sm text-secondary">Syncing...</span>
      </div>
    );
  }

  if (isFailed) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-2 w-2 rounded-full bg-red-500" />
        <span className="text-sm text-secondary">Couldn't sync</span>
        <button type="button" onClick={onReconnect} className="text-sm font-medium text-brand">
          Reconnect
        </button>
      </div>
    );
  }

  if (isDone) {
    return (
      <div className="flex items-center gap-2">
        <Check className="h-4 w-4 text-brand" />
        <span className="text-sm text-secondary">Synced just now · {refreshResult.refreshed_count} new</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" onClick={onRefresh} aria-label="Refresh" className="text-secondary hover:text-primary transition-colors">
                <RefreshCw className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Refresh</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-2 rounded-full bg-green-500" />
      <span className="text-sm text-secondary">Synced {timeAgo}</span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" onClick={onRefresh} aria-label="Refresh" className="text-secondary hover:text-primary transition-colors">
              <RefreshCw className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Refresh</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
